import { SongSearchResponse } from "@/models/SongSearchResponse";
import { SongsByLanguageCategory } from "@/models/SongsByLanguageCategory";
import { FESearchResult } from "@/models/frontend-only/FESearchResult";
import { LoadStatus } from "@/models/frontend-only/LoadStatus";

export const useSongSearchStore = defineStore("songsearch", () => {
  let loading = false;
  const lyricVideoMVPStore = useLyricVideoMVPStore();
  const lv = useLyricVideoMVPStore();
  const defaultQueryData: { [key: string]: any } = {
    numPerPage: 10,
    search: "",
    sortBy: null as null | string,
    page: 1,
    list: null as null | string,
    language: null as null | string[],
    expandToAllSongs: false,
    songcontent: null as null | string[],
    themes: null as null | string[],
    primarylanguage: null as null | string[],
    keys: null as null | string[],
    seo: null as null | string,
    seotitle: null as null | string,
  };

  const busy = useBusyStore();
  const betaStore = useBetaStore();
  const router = useRouter();
  const route = useRoute();
  const api = useApiStore();
  const { t } = useI18n();
  const debug = useDebug();
  const translations = useTranslationStore();

  const appStore = useAppStore();
  const path = ref();
  const status = ref<LoadStatus>(LoadStatus.Loading);
  // watch(status, () => {
  //   const statusLUT = {
  //     [LoadStatus.Loading]: "Loading",
  //     [LoadStatus.Ready]: "Ready",
  //     [LoadStatus.Error]: "Error",
  //   };
  //   console.log("status-check", statusLUT[status.value]);
  // });
  const queryData = ref<{ [key: string]: any }>(cloneDeep(defaultQueryData));
  const responseData = ref<SongSearchResponse | null>(null);
  const items = ref<FESearchResult[]>([]);

  function resetSearch() {
    const routeListId = (route.query.list as string) || "";
    const routeLanguage = (route.query.language as string) || "";

    const queryData = ref(cloneDeep(defaultQueryData));
    queryData.value.list = routeListId || null;
    queryData.value.language = routeLanguage || null;
    responseData.value = null;
    loading = false;
    status.value = LoadStatus.Loading;
    return loadSongs();
  }

  let abortController: AbortController | null = null;

  function abort() {
    abortController?.abort();
  }

  async function loadSongs(overrideQuery?: { [key: string]: string }) {
    lv.getAllVideos();
    if (!overrideQuery) {
      loadQuery();
    }
    busy.showBusy();
    loading = true;
    status.value = LoadStatus.Loading;
    try {
      path.value = router.currentRoute.value;

      abort();
      abortController = new AbortController();

      const response = await api.post(
        "/GetSongSearchResults",
        overrideQuery || toRaw(queryData.value),
        {
          abort: abortController,
        },
      );
      debug.log("responseData.value", response);
      responseData.value = response;

      items.value = await Promise.all(
        responseData.value?.items.map(async (i) => {
          const songStore = useSongStore();
          const songVersion = await songStore.getSongVersions(i.songNumber);
          if (songVersion) {
            return {
              ...i,
              title: songVersion.SongTitle,
              artist: songVersion.Artist,
              album: songVersion.Album,
              soundSample: {
                audioUrl: songVersion?.SoundSampleURL,
                imageUrl: songVersion?.AlbumArtURL,
              },
              enableSongVersion: !!songVersion || false,
            };
          }
          return i;
        }) || [],
      );

      // todo
      if (
        betaStore.features.ccliTop100New &&
        responseData.value &&
        responseData.value.list?.startsWith("topterritory")
      ) {
        const lut = [
          {
            name: "United States",
            listId: "topterritory_1",
            regionToUse: t("S.LIT_PageDescriptionSEO_TopSongsRegion_US"),
          },
          {
            name: "United Kingdom",
            listId: "topterritory_3",
            regionToUse: t("S.LIT_PageDescriptionSEO_TopSongsRegion_UK"),
          },
          {
            name: "Australia",
            listId: "topterritory_4",
            regionToUse: t("S.LIT_PageDescriptionSEO_TopSongsRegion_AU"),
          },
          {
            name: "Canada",
            listId: "topterritory_2",
            regionToUse: t("S.LIT_PageDescriptionSEO_TopSongsRegion_CA"),
          },
        ];

        const country = lut.find((c) => c.listId === queryData.value.list);
        if (country) {
          responseData.value.title = t(
            "S.LIT_PageTitleSEO_MostReportedSongsCountry",
            [country.regionToUse],
          );
          responseData.value.description = t(
            "S.LIT_PageDescriptionSEO_MostReportedSongsCountry",
            [country.regionToUse],
          );
          responseData.value.img = null;
          responseData.value.imgAlt = null;
        }
      }

      if (responseData.value?.list === "topthemed_Christmas") {
        responseData.value.img = null;
        responseData.value.imgAlt = null;
      }

      if (
        responseData.value?.numResults === 1 &&
        responseData.value?.items[0].songNumber
      ) {
        router.replace({
          path: `/songs/${responseData.value?.items[0].songNumber}/${responseData.value?.items[0].slug}`,
          query: {},
        });
        status.value = LoadStatus.Ready;
        loading = false;
        return;
      }

      status.value = LoadStatus.Ready;
      if (!overrideQuery) {
        await updateQuery();
      }
    } catch (err) {
      if (
        ((err as any).code && (err as any).code === "ERR_CANCELED") ||
        ((err as any).message &&
          (err as any).message.includes("without reason"))
      ) {
        return;
      } else {
        if (!(err as Error).message.includes("aborted")) {
          status.value = LoadStatus.Error;
        }
      }
    }
    busy.hideBusy();
    loading = false;
  }

  async function updateQuery() {
    const queryObj: { [key: string]: string } = {};
    const data = queryData.value;
    Object.keys(data).forEach((k) => {
      if (data[k] !== defaultQueryData[k]) {
        if (k === "numPerPage") {
          queryObj["numPerPage"] = data[k].toString();
        } else if (k === "sortBy") {
          queryObj["sortBy"] = data[k];
        } else if (k === "page") {
          queryObj["page"] = data[k].toString();
        } else if (k === "search") {
          queryObj["search"] = data[k];
        } else if (k === "list") {
          queryObj["list"] = data[k];
        } else if (k === "language") {
          queryObj["language"] = data[k];
        } else if (k === "expandToAllSongs") {
          queryObj["expandToAllSongs"] = data[k] ? "1" : "0";
        } else if (k === "songcontent") {
          queryObj["songcontent"] = data[k];
        } else if (k === "themes") {
          queryObj["themes"] = data[k];
        } else if (k === "primarylanguage") {
          queryObj["primarylanguage"] = data[k];
        } else if (k === "keys") {
          queryObj["keys"] = data[k];
        } else if (k === "seo") {
          queryObj["seo"] = data[k];
        } else if (k === "seotitle") {
          queryObj["seotitle"] = data[k];
        } else if (k === "cat") {
          queryObj["cat"] = data[k];
        }
      }
    });

    if (path.value.fullPath.includes("search=") && queryObj.list) {
      delete queryObj.list;
    }
    if (queryObj.list) {
      delete queryObj.search;
    }
    await router.replace({ query: queryObj });
  }

  function loadQuery() {
    queryData.value = cloneDeep(defaultQueryData);
    Object.keys(route.query).forEach((k) => {
      if (k === "numPerPage") {
        queryData.value.numPerPage = parseInt(route.query[k] as string);
      } else if (k === "sortBy") {
        queryData.value.sortBy = route.query[k] as string;
      } else if (k === "page") {
        queryData.value.page = parseInt(route.query[k] as string);
      } else if (k === "search") {
        queryData.value.search = route.query[k] as string;
      } else if (k === "seo") {
        queryData.value.seo = route.query[k] as string;
      } else if (k === "seotitle") {
        queryData.value.seotitle = route.query[k] as string;
      } else if (k === "list") {
        queryData.value.list = route.query[k] as string;
      } else if (k === "language") {
        if (Array.isArray(route.query[k])) {
          queryData.value.language = route.query[k];
        } else {
          queryData.value.language = [route.query[k]];
        }
      } else if (k === "expandToAllSongs") {
        queryData.value.expandToAllSongs = (route.query[k] as string) !== "0";
      } else if (k === "songcontent") {
        if (Array.isArray(route.query[k])) {
          queryData.value.songcontent = route.query[k];
        } else {
          queryData.value.songcontent = [route.query[k]];
        }
      } else if (k === "themes") {
        if (Array.isArray(route.query[k])) {
          queryData.value.themes = route.query[k];
        } else {
          queryData.value.themes = [route.query[k]];
        }
      } else if (k === "primarylanguage") {
        if (Array.isArray(route.query[k])) {
          queryData.value.primarylanguage = route.query[k];
        } else {
          queryData.value.primarylanguage = [route.query[k]];
        }
      } else if (k === "keys") {
        if (Array.isArray(route.query[k])) {
          queryData.value.keys = route.query[k];
        } else {
          queryData.value.keys = [route.query[k]];
        }
      } else if (k === "cat") {
        queryData.value.cat = route.query[k] as string;
      }
    });
  }

  async function updateNumPerPage(numPerPage: number) {
    queryData.value.numPerPage = numPerPage;
    await updateQuery();
  }

  async function updatePage(page: number) {
    queryData.value.page = page;
    await updateQuery();
  }

  async function onSortByUpdate(sortBy: string | null) {
    queryData.value.sortBy = sortBy;
    await updateQuery();
  }

  async function toggleExpandOnNoResults() {
    queryData.value.expandToAllSongs = !queryData.value.expandToAllSongs;
    await updateQuery();
  }

  async function updateFilters(
    selectedFilters: { id: string; catId: string }[],
    expandToAllSongs: boolean,
  ) {
    queryData.value.songcontent = selectedFilters
      .filter((f) => f.catId.toLowerCase() === "songcontent")
      .map((f) => f.id);
    queryData.value.themes = selectedFilters
      .filter((f) => f.catId.toLowerCase() === "themes")
      .map((f) => f.id);
    queryData.value.primarylanguage = selectedFilters
      .filter((f) => f.catId.toLowerCase() === "primarylanguage")
      .map((f) => f.id);
    queryData.value.keys = selectedFilters
      .filter((f) => f.catId.toLowerCase() === "keys")
      .map((f) => f.id);

    queryData.value.expandToAllSongs = expandToAllSongs;
    queryData.value.page = 1;

    await updateQuery();
  }

  const noResults = computed(
    () => !responseData.value?.numResults && !filteredVideos.value.length,
  );

  const profile = useProfileStore();
  const title = computed(() => {
    const formatted = Intl.NumberFormat(profile.profile?.language ?? "en-US");
    const numResults =
      filteredVideos.value.length +
      ((responseData.value && responseData.value.numResults) || 0);

    if (showCategoryAll.value) {
      return `${formatted.format(numResults)} Results for '${queryData.value.search}'`;
    }
    if (queryData.value.seo === "MassSettings" && queryData.value.seotitle) {
      return t("R.LIT_SongResultsFor", {
        numResults: responseData.value?.numResults || 0,
        searchText:
          queryData.value.seotitle +
          " - " +
          t("S.LIT_LiturgySectionMassSettings"),
      });
    } else if (queryData.value.seo === "MassSettings") {
      return t("R.LIT_SongResultsFor", {
        numResults: responseData.value?.numResults || 0,
        searchText: t("S.LIT_LiturgySectionMassSettings"),
      });
    } else if (queryData.value.seo === "LiturgyMass") {
      return t("R.LIT_SongResultsFor", {
        numResults: responseData.value?.numResults || 0,
        searchText: t("S.LIT_LiturgySectionTheme"),
      });
    } else if (queryData.value.seo === "ResponsorialPsalmsYearA") {
      return t("R.LIT_SongResultsFor", {
        numResults: responseData.value?.numResults || 0,
        searchText:
          t("S.LIT_LiturgySectionResponsorialPsalms") +
          " - " +
          t("S.LIT_LiturgyLinkResponsorialPsalmsSeeYearA"),
      });
    } else if (queryData.value.seo === "ResponsorialPsalmsYearB") {
      return t("R.LIT_SongResultsFor", {
        numResults: responseData.value?.numResults || 0,
        searchText:
          t("S.LIT_LiturgySectionResponsorialPsalms") +
          " - " +
          t("S.LIT_LiturgyLinkResponsorialPsalmsSeeYearB"),
      });
    } else if (queryData.value.seo === "ResponsorialPsalmsYearC") {
      return t("R.LIT_SongResultsFor", {
        numResults: responseData.value?.numResults || 0,
        searchText:
          t("S.LIT_LiturgySectionResponsorialPsalms") +
          " - " +
          t("S.LIT_LiturgyLinkResponsorialPsalmsSeeYearC"),
      });
    }

    if (responseData.value?.title?.includes("®")) {
      return updateTrademark(responseData.value.title);
    }
    return responseData.value?.title;
  });

  function updateTitleAndDescription() {
    const title = [
      !noResults.value
        ? responseData.value?.title
        : status.value !== LoadStatus.Loading
          ? t("S.SearchResultsNoResultsTitle1")
          : t("S.HomePageTabTitle"),
    ]
      .filter((i) => !!i)
      .join(" | ");

    const description = [
      responseData.value?.description
        ? responseData.value?.description
        : t("S.MetaTagDescription"),
    ]
      .filter((i) => !!i)
      .join("");

    const queryList = route.query.list;

    if (route.query.language) {
      appStore.updateTitleAndDescription({
        title: title + " | " + t("S.PageTabTitle"),
        description: description,
      });
    } else if (queryList === "top100" || queryList === "popular") {
      appStore.updateTitleAndDescription({
        title: title + " | " + t("S.PageTabTitle"),
        description: description,
      });
    } else if (
      queryList?.includes("topterritory") ||
      queryList?.includes("topdecade") ||
      queryList?.includes("topthemed") ||
      queryList?.includes("theme_")
    ) {
      appStore.updateTitleAndDescription({
        title: title + " | " + t("S.PageTabTitle"),
        description: description,
      });
    } else if (queryData.value?.seo === "ResponsorialPsalms") {
      appStore.updateTitleAndDescription({
        title:
          t("S.LIT_PageTitleSEO_ResponsorialPsalms") +
          " | " +
          t("S.PageTabTitle"),
        description: t("SV.MES_ResponsorialPsalmsDescription"),
      });
    } else if (queryData.value?.seo === "ResponsorialPsalmsYearA") {
      appStore.updateTitleAndDescription({
        title:
          t("S.LIT_PageTitleSEO_ResponsorialPsalms") +
          " | " +
          t("S.LIT_LiturgyLinkResponsorialPsalmsSeeYearA") +
          " | " +
          t("S.PageTabTitle"),
        description: t("SV.MES_ResponsorialPsalmsDescription"),
      });
    } else if (queryData.value?.seo === "ResponsorialPsalmsYearB") {
      appStore.updateTitleAndDescription({
        title:
          t("S.LIT_PageTitleSEO_ResponsorialPsalms") +
          " | " +
          t("S.LIT_LiturgyLinkResponsorialPsalmsSeeYearB") +
          " | " +
          t("S.PageTabTitle"),
        description: t("SV.MES_ResponsorialPsalmsDescription"),
      });
    } else if (queryData.value?.seo === "ResponsorialPsalmsYearC") {
      appStore.updateTitleAndDescription({
        title:
          t("S.LIT_PageTitleSEO_ResponsorialPsalms") +
          " | " +
          t("S.LIT_LiturgyLinkResponsorialPsalmsSeeYearC") +
          " | " +
          t("S.PageTabTitle"),

        description: t("SV.MES_ResponsorialPsalmsDescription"),
      });
    } else if (queryData.value?.seo === "MassSettings") {
      appStore.updateTitleAndDescription({
        title:
          (route.query.seotitle ? route.query.seotitle + " | " : "") +
          t("S.LIT_PageTitleSEO_MassSettings") +
          " | " +
          t("S.PageTabTitle"),
        description: t("SV.MES_MassSettingsDescription"),
      });
    } else if (queryData.value?.seo === "LiturgyMass") {
      appStore.updateTitleAndDescription({
        title: t("SV.LIT_LiturgicalMusic") + " | " + t("S.PageTabTitle"),
        description: t("SV.MES_LiturgicalMusicDescription"),
      });
    } else {
      appStore.updateTitleAndDescription({
        title: title,
        description: description,
      });
    }
  }

  let songsByLanguageData: SongsByLanguageCategory[] = [];
  let songsByLanguageDataLastCodeUsed: string = "";

  async function getSongsByLanguageData() {
    if (
      !songsByLanguageData.length ||
      songsByLanguageDataLastCodeUsed != translations.currentLanguageCode
    ) {
      songsByLanguageData = await api.get("/GetSongsByLanguageData");
      songsByLanguageDataLastCodeUsed = translations.currentLanguageCode;
    }
    return songsByLanguageData;
  }

  const showCategoryAll = computed(
    () => queryData.value.cat === "all" && lyricVideoMVPStore.enable,
  );

  /**
   * Allows me to search for any words in the title, authors, or lyrics, and can be of any order
   * if any of the words doesn't match anything at all, then the search result has a 0 score.
   */
  const filteredVideos = computed(() => {
    const s = ((route.query.search as string) || "").toLowerCase();
    if (!s || s === "*") {
      return sortBy(lv.allVideos, (v) => v.title);
    }

    const results = lv.allVideos
      .map((f) => {
        const searchWeights: {
          key: string;
          values: string[];
          weight: number;
        }[] = [
          {
            key: "title",
            values: [f.title.toLowerCase()],
            weight: 1,
          },
          {
            key: "songnumber",
            values: [f.ccliSongNumber.toLowerCase()],
            weight: 1,
          },
          {
            key: "title_any_order",
            values: f.title.split(" ").map((s) => s.toLowerCase()),
            weight: 0.5,
          },
          {
            key: "authors",
            values: f.authors.map((a) => a.toLowerCase()),
            weight: 0.75,
          },
          {
            key: "authors_any_order",
            values: f.authors.flatMap((a) =>
              a.split(" ").map((s) => s.toLowerCase()),
            ),
            weight: 0.5,
          },
          {
            key: "lyrics_any_order",
            values: f.keyLyricSearchTerms,
            weight: 0.5,
          },
        ];

        const searchTerms: string[] = s.split(" ");
        let hasZeroMatch = false;

        const score = searchTerms.reduce((acc, term) => {
          const termScore = searchWeights.reduce((innerAcc, weight) => {
            const values = weight.values;
            const match = values.some((value) => value.includes(term));
            const exactMatch = values.some((value) => value === term);
            return (
              innerAcc +
              (match ? weight.weight : 0) +
              (exactMatch ? weight.weight + 0.25 : 0)
            );
          }, 0);

          // Check if this term has no matches across any weights
          if (termScore === 0) {
            hasZeroMatch = true;
          }

          return acc + termScore;
        }, 0);

        // If any term had zero matches, return null
        if (hasZeroMatch) {
          return { ...f, score: 0 };
        }

        return {
          ...f,
          score,
        };
      })
      .filter((f) => f.score > 0)
      .sort((a, b) => b.score - a.score);

    return results;
  });
  return {
    showCategoryAll,
    filteredVideos,
    resetSearch,
    getSongsByLanguageData,
    status,
    responseData,
    queryData,
    noResults,
    title,
    onSortByUpdate,
    toggleExpandOnNoResults,
    updateFilters,
    updateNumPerPage,
    updatePage,
    loadSongs,
    updateTitleAndDescription,
    updateQuery,
    abort,
    items,
  };
});
